/**
 * Helpers for user/viewdetails club payload (same field fallbacks as Vibes mobile web).
 */
import {
  PLACEHOLDER_BANNER_URL,
  PLACEHOLDER_LOGO_URL,
} from "../constants/mediaPlaceholders";
export function resolvePublicAssetUrl(pathOrUrl) {
  if (pathOrUrl == null || pathOrUrl === "") return "";
  const s = String(pathOrUrl).trim();
  if (!s) return "";
  if (/^(https?:|data:|blob:)/i.test(s)) return s;
  if (/^\/\//.test(s)) return s;
  const base = (process.env.REACT_APP_BASE_URL || "").replace(/\/$/, "");
  const rel = s.startsWith("/") ? s.slice(1) : s;
  return base ? `${base}/${rel}` : s;
}

export function isApiSuccess(status) {
  return (
    status === true ||
    status === 1 ||
    status === "true" ||
    status === "1"
  );
}

/** Flatten `user/viewdetails` payloads so `tickets` and core fields live on one object. */
export function normalizeViewDetailsEvent(res) {
  if (!res || typeof res !== "object") return null;
  let data = res.data;
  if ((!data || typeof data !== "object") && (res._id || res.name)) {
    data = res;
  }
  if (!data || typeof data !== "object") return null;

  let event = data;
  if (data.event && typeof data.event === "object") {
    event = { ...data.event, ...data };
  }
  if (data.eventDetail && typeof data.eventDetail === "object") {
    event = { ...data.eventDetail, ...data, ...event };
  }
  if (data.details && typeof data.details === "object" && (data.details._id || data.details.name)) {
    event = { ...data.details, ...data, ...event };
  }

  const ticketCandidates = [
    event.tickets,
    event.ticketList,
    event.eventTickets,
    data.tickets,
    data.ticketList,
    res.tickets,
    res.ticketList,
  ];
  for (const list of ticketCandidates) {
    if (Array.isArray(list) && list.length) {
      event = { ...event, tickets: list };
      break;
    }
  }

  return event;
}

/** Event record from `user/viewdetails` (normalized `data`). */
export function resolveViewDetailsEvent(res) {
  const normalized = normalizeViewDetailsEvent(res);
  if (normalized) return normalized;
  const data = res?.data;
  if (data && typeof data === "object" && !Array.isArray(data)) return data;
  return null;
}

export function isViewDetailsResponseOk(res) {
  const event = resolveViewDetailsEvent(res);
  return Boolean(event?._id || event?.id);
}

export function pickImageUrl(item) {
  if (!item || typeof item !== "object") return "";
  let raw = "";
  const photos = item.photos;
  if (Array.isArray(photos) && photos.length > 0) {
    const p = photos[0];
    if (typeof p === "string") raw = p;
    else raw = p?.url || p?.uri || "";
  } else {
    const img = item.image;
    if (typeof img === "string") raw = img;
    else if (img && typeof img === "object") {
      raw = img.uri || img.url || "";
    }
  }
  if (!raw) {
    raw =
      item.thumbnail ||
      item.thumb ||
      item.photo ||
      item.imageUrl ||
      item.banner ||
      item.businessBanner ||
      item.boothImage ||
      item.boothPhoto ||
      "";
  }
  if (!raw && Array.isArray(item.booths) && item.booths.length > 0) {
    const firstBooth = item.booths[0] || {};
    raw =
      firstBooth.image ||
      firstBooth.imageUrl ||
      firstBooth.thumbnail ||
      firstBooth.photo ||
      "";
  }
  return raw ? resolvePublicAssetUrl(raw) : "";
}

/** Floor plan image on booth records (string, array, or { url }). */
export function pickFloorLayoutUrl(item) {
  if (!item || typeof item !== "object") return "";
  const raw =
    item.floorLayout ??
    item.floorLayoutImage ??
    item.floor_layout ??
    item.floorPlan ??
    item.floor_plan ??
    item.florLayout ??
    "";
  if (Array.isArray(raw) && raw.length) {
    const first = raw[0];
    if (typeof first === "string") return resolvePublicAssetUrl(first);
    if (first && typeof first === "object") {
      const nested = first.url || first.uri || "";
      return nested ? resolvePublicAssetUrl(nested) : "";
    }
    return "";
  }
  if (typeof raw === "string" && raw.trim()) return resolvePublicAssetUrl(raw);
  if (raw && typeof raw === "object") {
    const nested = raw.url || raw.uri || "";
    return nested ? resolvePublicAssetUrl(nested) : "";
  }
  return "";
}

export function pickTitle(item) {
  if (!item || typeof item !== "object") return "Venue";
  return (
    item.businessName ||
    item.name ||
    item.title ||
    item.clubName ||
    "Venue"
  );
}

export function pickAddress(item) {
  return item.address || item.fullAddress || item.city || "";
}

export function pickId(item) {
  if (!item || typeof item !== "object") return "";
  return String(item._id || item.id || item.eventId || "");
}

export function pickEventsFromClub(data) {
  if (!data || typeof data !== "object") return [];
  const keys = ["events", "eventList", "hostEvents", "upcomingEvents", "event", "tables"];
  for (const k of keys) {
    const v = data[k];
    if (Array.isArray(v)) return v;
  }
  return [];
}

export function pickEventType(ev) {
  if (!ev || typeof ev !== "object") return "";
  return String(ev.type || ev.eventType || ev.category || ev.tableType || "").trim();
}

export function pickRating(item) {
  if (!item || typeof item !== "object") return null;
  const r = item.rating ?? item.avgRating ?? item.stars;
  if (r === undefined || r === null) return null;
  const n = Number(r);
  return Number.isFinite(n) ? n : null;
}

export function pickCapacityText(ev) {
  if (!ev || typeof ev !== "object") return "";
  const c =
    ev.capacity ??
    ev.eventCapacity ??
    ev.tableCapacity ??
    ev.maxGuest ??
    ev.maxGuests;
  const tables = ev.tables ?? ev.tableCount;
  if (c != null && c !== "" && tables) return `Capacity: ${c} · ${tables} tables`;
  if (c != null && c !== "") return `Capacity: ${c}`;
  if (tables) return `${tables} tables`;
  return "";
}

/** Logo / circular image — host profile uses businessPicture. Falls back to local placeholder. */
export function pickLogoUrl(data) {
  if (!data || typeof data !== "object") return PLACEHOLDER_LOGO_URL;
  const raw =
    data.businessPicture ||
    data.businessLogo ||
    data.businessProfile ||
    data.profilePicture ||
    data.logo ||
    data.profileImage ||
    data.avatar ||
    data.image?.url ||
    data.image?.uri ||
    "";
  if (typeof raw === "string" && raw.trim()) {
    return resolvePublicAssetUrl(raw.trim());
  }
  const fromPhotos = pickImageUrl(data);
  return fromPhotos || PLACEHOLDER_LOGO_URL;
}

/** Hero banner — host profile uses businessBanner. Falls back to local placeholder. */
export function pickBannerUrl(data) {
  if (!data || typeof data !== "object") return PLACEHOLDER_BANNER_URL;
  const raw = data.businessBanner || data.banner || "";
  if (typeof raw === "string" && raw.trim()) {
    return resolvePublicAssetUrl(raw.trim());
  }
  const fallback = pickImageUrl(data);
  return fallback || PLACEHOLDER_BANNER_URL;
}

/** URLs for club hero slider: banner + gallery photos (deduped). */
export function pickBannerSlideUrls(data) {
  if (!data || typeof data !== "object") return [PLACEHOLDER_BANNER_URL];
  const seen = new Set();
  const out = [];

  const push = (u) => {
    if (!u || typeof u !== "string") return;
    const s = u.trim();
    if (!s || seen.has(s)) return;
    seen.add(s);
    out.push(s);
  };

  const rawBanner = data.businessBanner || data.banner || "";
  if (typeof rawBanner === "string" && rawBanner.trim()) {
    push(resolvePublicAssetUrl(rawBanner.trim()));
  }

  const photos = data.photos;
  if (Array.isArray(photos)) {
    for (const p of photos) {
      const raw = typeof p === "string" ? p : p?.url || p?.uri || "";
      if (raw) push(resolvePublicAssetUrl(String(raw).trim()));
    }
  }

  if (!out.length) {
    const fb = pickImageUrl(data);
    if (fb) push(fb);
  }
  if (!out.length) out.push(PLACEHOLDER_BANNER_URL);
  return out;
}

/** Text after "Capacity:" for compact display. */
export function capacityValueOnly(ev) {
  const line = pickCapacityText(ev);
  if (!line) return "";
  return line.replace(/^Capacity:\s*/i, "").trim() || line;
}

/** Promoter id from event/booking URL query (`promoterId` or `promoter_id`). */
export function readPromoterIdFromSearch(searchParams) {
  if (!searchParams || typeof searchParams.get !== "function") return "";
  return String(searchParams.get("promoterId") || searchParams.get("promoter_id") || "").trim();
}

/** Append promoterId to an existing query string or URLSearchParams. */
export function appendPromoterIdToSearchParams(params, promoterId) {
  const next =
    params instanceof URLSearchParams
      ? new URLSearchParams(params.toString())
      : new URLSearchParams(String(params || "").replace(/^\?/, ""));
  const id = String(promoterId || "").trim();
  if (id) next.set("promoterId", id);
  return next;
}

/** Normalize API `type` (e.g. "Club", "VIP Entry") for tab matching. */
export function normalizeEventType(ev) {
  return pickEventType(ev).toLowerCase().trim();
}

function isTruthyFlag(value) {
  if (typeof value === "boolean") return value;
  const normalized = String(value || "").trim().toLowerCase();
  return normalized === "true" || normalized === "1" || normalized === "yes";
}

export function isVipEntryEvent(ev) {
  const t = normalizeEventType(ev);
  return t === "vip entry" || t === "vipentry" || t === "vip";
}

export function isBottleServiceEvent(ev) {
  if (!ev || typeof ev !== "object") return false;
  const typeMatches = [
    ev.type,
    ev.eventType,
    ev.category,
    ev.tableType,
    ev.serviceType,
    ev.subType,
  ]
    .map((v) => String(v || "").trim().toLowerCase())
    .some((value) => value === "bottle service" || value === "bottleservice" || value === "bottle");
  if (typeMatches) return true;
  return (
    isTruthyFlag(ev.bottleService) ||
    isTruthyFlag(ev.isBottleService) ||
    isTruthyFlag(ev.hasBottleService) ||
    isTruthyFlag(ev.bottle_service)
  );
}

export function getBottleServiceHeroImageUrl() {
  const raw = String(process.env.REACT_APP_BOTTLE_SERVICE_HERO_IMAGE || "").trim();
  if (raw) return resolvePublicAssetUrl(raw);
  const base = (process.env.PUBLIC_URL || "").replace(/\/$/, "");
  return `${base}/assets/images/bottle-service-banner.png`;
}

/**
 * Filter by tab vs API `events[].type`.
 * Club tab shows all host events; other tabs filter by type.
 */
export function eventMatchesCategoryTab(ev, tabKey) {
  const t = normalizeEventType(ev);
  if (tabKey === "club") return true;
  if (tabKey === "booth") return t === "booth";
  if (tabKey === "event") return t === "event";
  if (tabKey === "table") return t === "table";
  if (tabKey === "vip") {
    return t === "vip" || t === "vipentry" || t.includes("vip");
  }
  if (tabKey === "bottle") return isBottleServiceEvent(ev);
  return false;
}
